import { useEffect, useReducer, useRef, useState } from "react";

import DownloadIcon from "@mui/icons-material/Download";
import { Box, CircularProgress, Divider, IconButton, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import moment from "moment-timezone";
import { useDispatch } from "react-redux";

import { FilterAutocomplete } from "~/components/shared";
import { setDownloadImageTime } from "~/store/generalSlice";
import DashboardPPTCharts from "./DashboardPPTCharts";
import { getNormalizedAuthorizations, NormalizedAuthorization } from "./dashboardPPTHelpers";
import { useDashboardPPTProvider } from "./DashboardPPTProvider";

export type DashboardPPTFilters = {
  startDate: moment.Moment | null;
  endDate: moment.Moment | null;
  authorization: any | null;
  errors: { [key: string]: string };
};

const DashboardPPT = () => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const { authorizations, authorizationOptions, error, isLoading } = useDashboardPPTProvider();

  const isFiltersInitialized = useRef(false);

  const [selectedAuthorization, setSelectedAuthorization] = useState<NormalizedAuthorization | null>(null);

  const [filters, setFilters] = useReducer(
    (state: DashboardPPTFilters, action: { key: keyof DashboardPPTFilters; value: any }) => {
      let response: DashboardPPTFilters;

      if (action.key === "errors") {
        const newErrors = { ...state.errors };

        if (action.value.value) {
          newErrors[action.value.key] = action.value.value;
        } else {
          delete newErrors[action.value.key];
        }

        response = { ...state, errors: newErrors };
      } else {
        response = { ...state, [action.key]: action.value };

        if (action.key === "authorization") {
          response.startDate = response.authorization ? moment(response.authorization.startDate) : null;
          response.endDate = response.authorization ? moment(response.authorization.stopDate) : null;
        }

        const normalizedAuths = getNormalizedAuthorizations(authorizations, response.startDate ?? undefined, response.endDate ?? undefined);
        setSelectedAuthorization(
          normalizedAuths.find((auth) => auth.authorizationServiceId === response.authorization?.id) as NormalizedAuthorization | null,
        );
      }

      return response;
    },
    {
      startDate: null,
      endDate: null,
      authorization: null,
      errors: {},
    },
  );

  useEffect(() => {
    if (!isLoading && !error && !isFiltersInitialized.current && authorizationOptions.length) {
      setFilters({
        key: "authorization",
        value: authorizationOptions.at(0) ?? null,
      });
    }
  }, [isLoading, error, authorizationOptions]);

  return (
    <Stack
      direction="column"
      sx={{ overflow: isLoading ? "hidden" : "auto", p: 1, width: "100%", height: "100%", position: "relative", gap: 0.5 }}
    >
      <Stack direction="row" sx={{ flexWrap: "wrap" }}>
        <FilterAutocomplete
          chipTooltipKey="description"
          defaultValue={null}
          disableCloseOnSelect={false}
          filterKey="authorization"
          filters={filters}
          groupKey="status"
          label="Service"
          multiple={false}
          options={authorizationOptions}
          setFilters={setFilters}
          sx={{
            flexGrow: 1,
            width: "25%",
          }}
          value={filters.authorization}
        />
        <Divider orientation="vertical" sx={{ ml: 1, mr: 0.5 }} />
        <Tooltip sx={{ alignContent: "center", height: "fit-content" }} title="Export Dashboard as Image">
          <IconButton
            onClick={() => {
              dispatch(setDownloadImageTime(new Date().getTime()));
            }}
            size="small"
          >
            <DownloadIcon />
          </IconButton>
        </Tooltip>
      </Stack>
      <Divider />
      <Box sx={{ flexGrow: 1, overflow: "auto", p: 1, alignContent: "center" }}>
        <Stack
          direction="column"
          sx={{
            p: 1,
            m: -1,
            gap: 2,
            position: "relative",
            overflow: isLoading ? "hidden" : "auto",
            height: isLoading || !authorizationOptions.length ? "100%" : "auto",
            minHeight: "100%",
          }}
        >
          {isLoading || !authorizationOptions.length || error ? (
            <Stack direction="column" overflow="hidden" position="absolute" sx={{ inset: 0 }}>
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  position: "relative",
                  alignContent: "center",
                  textAlign: "center",
                  background: theme.palette.background.paper,
                  zIndex: theme.zIndex.drawer - 1,
                }}
              >
                {!error && <CircularProgress size={150} />}
                <Typography sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}>
                  {error ? error : "Loading..."}
                </Typography>
              </Box>
            </Stack>
          ) : (
            <DashboardPPTCharts authorization={selectedAuthorization} filters={filters} setFilters={setFilters} />
          )}
        </Stack>
      </Box>
    </Stack>
  );
};

export default DashboardPPT;
